// EntityEmailDialog — диалог «Отправить письмо» с карточки сделки или лида
// (V15-entity-email W2; канон InvoiceEmailDialog, без его переписывания).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { emailApi, type SendEmailResponse } from '@/api/email';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Справочники шаблонов/аккаунтов в диалоге (размер ≤100, канон InvoiceEmailDialog). */
const OPTIONS_PAGE_SIZE = 100;

/**
 * Пропсы диалога отправки письма с карточки сделки или лида.
 *
 * @param open — открыт ли диалог.
 * @param onClose — закрытие пользователем.
 * @param recipientEmail — адрес получателя (уже проверен на странице; дубль гварда — BE 400).
 * @param preview — POST /api/email/templates/preview без invoiceId.
 * @param send — POST /api/deals|leads/{id}/send-email → 202.
 */
export interface EntityEmailDialogProps {
  open: boolean;
  onClose: () => void;
  recipientEmail: string;
  preview: (templateId: string) => Promise<{ subject: string; body: string }>;
  send: (payload: { templateId: string; accountId?: string }) => Promise<SendEmailResponse>;
}

/** Диалог «Отправить письмо» с карточки сделки или лида (зеркало InvoiceEmailDialog, M-3). */
export function EntityEmailDialog({
  open,
  onClose,
  recipientEmail,
  preview,
  send,
}: EntityEmailDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<{ subject: string; body: string } | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: () => emailApi.listTemplates(0, OPTIONS_PAGE_SIZE),
    enabled: open,
    retry: false,
  });
  const settingsQuery = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: () => emailApi.getSettings(),
    enabled: open,
    retry: false,
  });

  const templates = templatesQuery.data?.content ?? [];
  const accounts = settingsQuery.data?.accounts ?? [];
  const defaultAccountId = settingsQuery.data?.activeAccountId ?? accounts[0]?.id ?? null;
  const selectedTemplateId = templateId ?? templates[0]?.id ?? null;
  const selectedAccountId = accountId ?? defaultAccountId;

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      return preview(selectedTemplateId);
    },
    onSuccess: (data) => setPreviewResult({ subject: data.subject, body: data.body }),
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      return send({
        templateId: selectedTemplateId,
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email'] });
    },
  });

  const isPending = sendMutation.isPending || previewMutation.isPending;
  const sendError = sendMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(sendMutation.error, t('email.send.error'))}</Alert>
  ) : null;
  const previewError = previewMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(previewMutation.error, t('email.templates.previewError'))}</Alert>
  ) : null;
  const sendResult =
    sendMutation.data && sendMutation.data.status === 'FAILED' ? (
      <Alert severity="error">
        {t('email.send.failed', { error: sendMutation.data.error ?? t('email.send.error') })}
      </Alert>
    ) : sendMutation.data && sendMutation.data.status === 'SENT' ? (
      <Alert severity="success">{t('email.send.success')}</Alert>
    ) : null;

  const noAccounts = !settingsQuery.isLoading && accounts.length === 0;
  const noTemplates = !templatesQuery.isLoading && templates.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('email.send.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {sendError}
          {sendResult}
          <TextField
            label={t('email.send.recipient')}
            value={recipientEmail}
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-label': t('email.send.recipient'), readOnly: true }}
          />
          <TextField
            select
            label={t('email.send.template')}
            value={selectedTemplateId ?? ''}
            onChange={(e) => {
              setTemplateId(e.target.value || null);
              setPreviewResult(null);
            }}
            inputProps={{ 'aria-label': t('email.send.template') }}
          >
            {templates.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          {noAccounts ? (
            <Alert severity="warning">{t('email.send.notConfigured')}</Alert>
          ) : (
            <TextField
              select
              label={t('email.send.account')}
              value={selectedAccountId ?? ''}
              onChange={(e) => {
                setAccountId(e.target.value || null);
              }}
              inputProps={{ 'aria-label': t('email.send.account') }}
            >
              {accounts.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.fromEmail}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => previewMutation.mutate()}
              disabled={isPending || !selectedTemplateId || noAccounts}
            >
              {t('email.send.preview')}
            </Button>
          </Stack>

          {previewError}
          {previewResult ? (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1">{previewResult.subject}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {previewResult.body}
              </Typography>
            </Box>
          ) : null}

          {noTemplates ? <Alert severity="warning">{t('email.send.noTemplates')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => sendMutation.mutate()}
          disabled={isPending || sendMutation.isSuccess || !selectedTemplateId || noAccounts || noTemplates}
        >
          {t('email.send.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
